import { createUseStyles } from "react-jss";
import { FormProvider, useForm } from "react-hook-form";
import { useEffect } from "react";
import { theme } from "$utils/theme/bataillog";
import { MonStockConfigType, StockItConfigType, WMSConfigType } from "$types";
import { Button, Text } from "$components";
import CField, { CFieldInputProps } from "$forms/CField";
import useJStyles from "$hooks/useJStyles";
import useAuth from "$hooks/useAuth";
import WMSConfigEnum from "$types/WMSConfig";
import GenericSelect from "$forms/GenericSelect";
import WarehouseSelect from "$forms/WarehouseSelect";
import StockItConfig from "./form/StockItConfig";
import MonStockConfig from "./form/MonStockConfig";

type Props = {
  onSubmit: (data: any) => Promise<any>;
  onReset: () => void;
  wmsConfig?: WMSConfigType<StockItConfigType | MonStockConfigType>;
  wmsConfigError?: any;
  isSubmitting?: boolean;
};

function WMSConfigForm({
  onSubmit,
  onReset,
  wmsConfig,
  wmsConfigError,
  isSubmitting,
}: Props) {
  const { isAdmin } = useAuth();
  const classes = useStyles();
  const { jclasses } = useJStyles();
  const defaultValues: WMSConfigType<StockItConfigType | MonStockConfigType> =
    wmsConfig
      ? {
          ...wmsConfig,
        }
      : {
          _id: null,
          type: null,
          warehouses: null,
          config: null,
        };

  const formMethods = useForm<
    WMSConfigType<StockItConfigType | MonStockConfigType>
  >({
    mode: "onChange",
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    getValues,
    watch,
  } = formMethods;

  watch("type", defaultValues.type);
  const selectedWarehouses = watch("warehouses", defaultValues.warehouses);

  useEffect(() => {
    if (getValues().type === WMSConfigEnum.MonStock) {
      const currentValues = getValues();
      const newConfig = currentValues.warehouses.map<{
        warehouseId: string;
        code: string;
      }>((warehouse) => {
        const wIndex = (
          currentValues.config as MonStockConfigType
        )?.warehouseProviders.findIndex((wp) => wp.warehouseId === warehouse);
        if (wIndex > -1) {
          return {
            warehouseId: warehouse,
            code: (currentValues.config as MonStockConfigType)
              ?.warehouseProviders[wIndex].code,
          };
        }
        return {
          warehouseId: warehouse,
          code: undefined,
        };
      });
      setValue("config.warehouseProviders", newConfig);
    }
  }, [getValues, selectedWarehouses, setValue]);

  const configForm = () => {
    const { type } = getValues();

    switch (type) {
      case WMSConfigEnum.MonStock:
        return <MonStockConfig />;
      case WMSConfigEnum.StockIt:
        return <StockItConfig />;
      default:
        return null;
    }
  };
  const handleMultipleWarehouse = () => {
    const { type } = getValues();
    return type === WMSConfigEnum.MonStock;
  };

  return (
    <div className={jclasses.jWrapper}>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)} className={jclasses.form}>
          <div className={jclasses.formTop}>
            <Text type="h2">
              {wmsConfig
                ? "Éditer la configuration WMS"
                : "Ajouter une configuration WMS"}
            </Text>
          </div>

          <div className={jclasses.formSectionHead}>
            <Text type="h3">Informations générales</Text>
          </div>

          {/*  - select */}
          <div className={classes.newFormFieldsRow}>
            <CField control={control} name="type" required error={errors.type}>
              {(props: CFieldInputProps) => (
                <GenericSelect
                  {...props}
                  label="Nom du logiciel de gestion"
                  onChange={(value) => setValue("type", value)}
                  options={[
                    {
                      value: WMSConfigEnum.MonStock,
                      label: WMSConfigEnum.MonStock,
                    },
                    {
                      value: WMSConfigEnum.StockIt,
                      label: WMSConfigEnum.StockIt,
                    },
                  ]}
                />
              )}
            </CField>
          </div>

          <div className={classes.newFormFieldsRow}>
            <CField
              control={control}
              name="warehouses"
              required
              error={errors.warehouses}
            >
              {(props: CFieldInputProps) => (
                <WarehouseSelect // TODO: ajouter le company id quand on est client
                  {...props}
                  label={
                    handleMultipleWarehouse()
                      ? "Entrepôt(s) associé(s)"
                      : "Entrepôt associé"
                  }
                  placeholder=""
                  onChange={(value) => {
                    setValue("warehouses", value);
                  }}
                  disabled={!getValues().type}
                  multiple={handleMultipleWarehouse()}
                  allowNoCompanyFilter={isAdmin()}
                />
              )}
            </CField>
          </div>

          <div className={jclasses.formSectionHead}>
            <Text type="h3">Configuration du WMS</Text>
          </div>

          {/* Formulaire de configuration */}
          {configForm()}

          {!!wmsConfigError && (
            <Text className={classes.errorContainer}>{wmsConfigError}</Text>
          )}

          {isAdmin() && (
            <div className={jclasses.footerForm}>
              <Button color="secondary" onClick={onReset}>
                Annuler
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                Enregistrer
              </Button>
            </div>
          )}
        </form>
      </FormProvider>
    </div>
  );
}

export default WMSConfigForm;

const useStyles = createUseStyles(() => ({
  errorContainer: {
    color: theme.color.red,
    marginBottom: 20,
  },
  newFormFieldsRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 20,
    marginTop: 20,
  },
}));
